import { Box, Button, Container, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect } from 'react';

import DiscountBanner from './landingPage/discountBanner';
import FooterSection from './footerSection';
import Navbar from 'src/components/navbar/navbar';
import TypingEffect from 'src/components/typingEffect';
import backgroundImage from "../resources/images/hero_img_bg.jpg";
import bannerBgImage from "../resources/images/img_3.jpg";
import { content } from 'src/common/lang/en-US';
import { motion } from 'framer-motion';

const ContactPage: React.FC = () => {
    const { heading, headingContent, formFields, contactDetails, moreInfo } = content.contactPage;

    useEffect(() => {
        window.scrollTo({ top: 0 })
    }, []);

    return (
        <Box sx={{ position: 'relative', overflowX: 'hidden', bgcolor: "background.iceGray" }}>
            {/* Hero Section */}
            <Box sx={{ position: 'relative', height: '100vh' }}>
                {/* Parallax Background */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: `url(${backgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        zIndex: 0,
                    }}
                />
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        height: '100%',
                        display: 'flex',
                        flexDirection: "column",
                        alignItems: 'center',
                        color: '#fff',
                        textAlign: 'center',
                        background: 'rgba(0, 0, 0, 0.3)',
                    }}
                >
                    <Navbar />
                    <Container sx={{ mt: 20 }}>
                        <Typography variant="h1_Thin" component="h1" gutterBottom>
                            {heading}{"\u00A0"}
                            <TypingEffect
                                words={headingContent}
                                typographySX={{
                                    color: '#ea728c',
                                    fontSize: '4rem',
                                }}
                                varient="h1_Thin"
                            />
                        </Typography>
                    </Container>
                </Box>
            </Box>

            {/* Content Section with animation */}
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                viewport={{ once: true, amount: 0.2 }}
                style={{ zIndex: 2, position: 'relative' }}
            >
                <Box sx={{ py: 10, px: { xs: 4, sm: 10 }, backgroundColor: 'inherit', zIndex: 2, position: 'relative' }}>
                    <Container maxWidth="lg">
                        <Box
                            display="grid"
                            gap={10}
                            gridTemplateColumns={{
                                xs: '1fr',
                                sm: '2.5fr 1.5fr',
                                md: '2.5fr 1.5fr',
                            }}
                        >
                            <Stack height="36rem" mt={-20} direction="column" gap={2} bgcolor="primary.light" p={6}>
                                {/* Form Fields */}
                                <motion.div
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, ease: 'easeOut' }}
                                >
                                    <Stack direction="row" width="100%" gap={2}>
                                        <Stack gap={1} direction="column" width="50%">
                                            <Typography variant="h6_Thin">{formFields.firstName}</Typography>
                                            <TextField id="outlined-basic" variant="outlined" />
                                        </Stack>
                                        <Stack gap={1} direction="column" width="50%">
                                            <Typography variant="h6_Thin">{formFields.lastName}</Typography>
                                            <TextField id="outlined-basic" variant="outlined" />
                                        </Stack>
                                    </Stack>
                                    <Stack gap={1} direction="column">
                                        <Typography variant="h6_Thin">{formFields.email}</Typography>
                                        <TextField id="outlined-basic" variant="outlined" />
                                    </Stack>
                                    <Stack gap={1} direction="column">
                                        <Typography variant="h6_Thin">{formFields.subject}</Typography>
                                        <TextField id="outlined-basic" variant="outlined" />
                                    </Stack>
                                    <Stack gap={1} direction="column">
                                        <Typography variant="h6_Thin">{formFields.message}</Typography>
                                        <TextField id="outlined-basic" variant="outlined" />
                                    </Stack>
                                    <Button
                                        sx={{
                                            backgroundColor: '#ea728c',
                                            color: '#FFFFFF',
                                            borderRadius: '30px',
                                            padding: '18px 30px',
                                            mt: 4,
                                            fontSize: '14px',
                                            width: '10rem',
                                        }}
                                    >
                                        Send Message
                                    </Button>
                                </motion.div>
                            </Stack>

                            {/* Contact Details and More Info */}
                            <Stack gap={2}>
                                <motion.div
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, ease: 'easeOut' }}
                                >
                                    {/* Contact Details */}
                                    <Stack direction="column" gap={2} bgcolor="primary.light" p={6}>
                                        {contactDetails.map((contact) =>
                                            <Stack direction="column" gap={1}>
                                                <Typography variant="h6">{contact.title}</Typography>
                                                <Typography variant="h6_Thin">{contact.content}</Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                    {/* More Info */}
                                    <Stack direction="column" gap={2} bgcolor="primary.light" p={6}>
                                        <Typography variant="h6">{moreInfo.title}</Typography>
                                        <Typography variant="h6_Thin">{moreInfo.content}</Typography>
                                        <Button
                                            sx={{
                                                backgroundColor: '#ea728c',
                                                color: '#FFFFFF',
                                                borderRadius: '30px',
                                                px: '1.5rem',
                                                py: '0.5rem',
                                                mt: 4,
                                                fontSize: '14px',
                                                width: '8rem',
                                            }}
                                        >
                                            Learn More
                                        </Button>
                                    </Stack>
                                </motion.div>
                            </Stack>
                        </Box>
                    </Container>
                </Box>
            </motion.div>

            {/* Discount banner component */}
            <Box
                sx={{
                    position: 'relative',
                    height: '60vh',
                    backgroundImage: `url(${bannerBgImage})`,
                    backgroundSize: 'cover',
                    backgroundAttachment: 'fixed',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(234, 114, 140, 0.9)',
                        zIndex: 1,
                    }}
                />
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    <DiscountBanner />
                </Box>
            </Box>

            {/* Footer section */}
            <FooterSection />
        </Box>
    );
}

export default ContactPage;
